import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PenNib, Plus, Trash, PencilSimple, X, User, UserCircle, Buildings, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Signatarios() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [municipios, setMunicipios] = useState([]);
  const [selectedMun, setSelectedMun] = useState(user?.municipio_id || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      api.get("/municipalities?limit=500").then((r) => setMunicipios(r.data || []));
    } else if (user?.municipio_id) {
      setSelectedMun(user.municipio_id);
    }
  }, [isAdmin, user]);

  const load = () => {
    if (!selectedMun) { setItems([]); return; }
    setLoading(true);
    api.get(`/signatarios?municipio_id=${selectedMun}`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [selectedMun]);

  const municipioNome = useMemo(() => {
    if (!isAdmin) return user?.municipio_nome || "";
    return (municipios.find((m) => m.ibge_code === selectedMun) || {}).name || "";
  }, [municipios, selectedMun, isAdmin, user]);

  const excluir = async (s) => {
    if (s.source === "user") {
      toast.info("Este assinante vem do cadastro de usuários. Para removê-lo, gerencie em 'Gerenciar Acessos'.");
      return;
    }
    if (!window.confirm(`Excluir o assinante "${s.nome}" (${s.cargo})?`)) return;
    try {
      await api.delete(`/signatarios/${s.id}`);
      toast.success("Assinante excluído");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const manuais = items.filter((s) => s.source === "manual");
  const usuarios = items.filter((s) => s.source === "user");

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase-label flex items-center gap-1.5">
            <PenNib size={12} weight="duotone" className="text-[#F59E0B]" />
            Assinantes de Documentos
          </div>
          <h1 className="font-heading text-4xl tracking-tight mt-2">Gestão de Assinantes</h1>
          <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl">
            Cadastre autoridades responsáveis pela assinatura de documentos oficiais (Prefeito, Secretários, Ordenadores).
            Usuários do sistema com login já aparecem automaticamente como assinantes disponíveis.
          </p>
        </div>
        <button
          data-testid="new-signatario"
          onClick={() => { setEditing(null); setShowForm(true); }}
          disabled={!selectedMun}
          className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium flex items-center gap-2 disabled:opacity-50">
          <Plus size={16} weight="bold" /> Cadastrar assinante
        </button>
      </div>

      {isAdmin && (
        <div className="tce-card p-5">
          <label className="uppercase-label block mb-2 flex items-center gap-1.5">
            <Buildings size={12} weight="duotone" className="text-[#F59E0B]" />
            Município
          </label>
          <select
            data-testid="signatario-muni-select"
            value={selectedMun}
            onChange={(e) => setSelectedMun(e.target.value)}
            className="tce-input w-full max-w-md px-3 py-2.5 rounded-md text-sm">
            <option value="">Selecione um município…</option>
            {municipios.map((m) => (
              <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>
            ))}
          </select>
        </div>
      )}

      {!selectedMun ? (
        <div className="tce-card p-10 text-center text-[#94A3B8]">
          <PenNib size={40} weight="duotone" className="mx-auto text-[#334155] mb-3" />
          {isAdmin ? "Selecione um município para visualizar os assinantes." : "Você não está vinculado a um município."}
        </div>
      ) : loading ? (
        <div className="text-[#94A3B8]">Carregando…</div>
      ) : (
        <>
          {items.length === 0 && (
            <div className="rounded-md bg-[#EF4444]/10 border border-[#EF4444]/40 p-5 flex items-start gap-3">
              <Warning size={20} weight="fill" className="text-[#EF4444] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[#FCA5A5] font-medium mb-1">Nenhum assinante cadastrado para {municipioNome}</div>
                <div className="text-xs text-[#94A3B8]">
                  Nenhum documento oficial poderá ser gerado sem assinante. Clique em "Cadastrar assinante" para adicionar o primeiro (ex: Prefeito).
                </div>
              </div>
            </div>
          )}

          {manuais.length > 0 && (
            <SignatariosTable title="Assinantes cadastrados" items={manuais} onEdit={(s) => { setEditing(s); setShowForm(true); }} onDelete={excluir} />
          )}

          {usuarios.length > 0 && (
            <SignatariosTable title="Usuários do sistema (importados automaticamente)" items={usuarios} onDelete={excluir} readOnly />
          )}
        </>
      )}

      {showForm && (
        <SignatarioForm
          municipioId={selectedMun}
          municipioNome={municipioNome}
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SignatariosTable({ title, items, onEdit, onDelete, readOnly }) {
  return (
    <div className="tce-card overflow-hidden">
      <div className="px-5 py-4 border-b border-[#334155] flex items-center justify-between">
        <div className="uppercase-label">{title}</div>
        <div className="text-xs text-[#94A3B8]">{items.length} pessoa(s)</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1E293B]">
            <tr className="text-left">
              <th className="px-5 py-3 uppercase-label">Nome</th>
              <th className="px-5 py-3 uppercase-label">Cargo</th>
              <th className="px-5 py-3 uppercase-label">CPF / Email</th>
              <th className="px-5 py-3 uppercase-label">Origem</th>
              <th className="px-5 py-3 uppercase-label text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s, i) => (
              <tr key={s.id} data-testid={`signatario-row-${i}`} className="border-t border-[#334155] hover:bg-[#1E293B]/60">
                <td className="px-5 py-3.5 text-[#E9EEF5] font-medium">{s.nome}</td>
                <td className="px-5 py-3.5 text-[#94A3B8]">{s.cargo}</td>
                <td className="px-5 py-3.5 text-[#94A3B8] font-mono text-xs">{s.cpf || s.email || "—"}</td>
                <td className="px-5 py-3.5">
                  {s.source === "user" ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#8B5CF6]/15 text-[#8B5CF6]">
                      <UserCircle size={11} weight="fill" /> Usuário
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#F59E0B]/15 text-[#F59E0B]">
                      <User size={11} weight="fill" /> Manual
                    </span>
                  )}
                </td>
                <td className="px-5 py-3.5 text-right">
                  {!readOnly && onEdit && (
                    <button
                      data-testid={`signatario-edit-${i}`}
                      onClick={() => onEdit(s)}
                      title="Editar"
                      className="p-1.5 rounded text-[#94A3B8] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors mr-1"
                    >
                      <PencilSimple size={14} weight="duotone" />
                    </button>
                  )}
                  {!readOnly && (
                    <button
                      data-testid={`signatario-delete-${i}`}
                      onClick={() => onDelete(s)}
                      title="Excluir"
                      className="p-1.5 rounded text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
                    >
                      <Trash size={14} weight="duotone" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SignatarioForm({ municipioId, municipioNome, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    nome: initial?.nome || "",
    cargo: initial?.cargo || "",
    cpf: initial?.cpf || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.cargo.trim()) {
      toast.error("Preencha nome e cargo");
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) {
        await api.patch(`/signatarios/${initial.id}`, form);
        toast.success("Assinante atualizado");
      } else {
        await api.post("/signatarios", { ...form, municipio_id: municipioId });
        toast.success("Assinante cadastrado");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const cargosSugeridos = ["Prefeito Municipal", "Vice-Prefeito", "Secretário Municipal", "Ordenador de Despesas", "Fiscal do Contrato", "Procurador Municipal", "Controlador Interno", "Gestor do Contrato"];

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tce-card w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
          <div>
            <div className="uppercase-label">{initial ? "Editar" : "Novo"} assinante</div>
            <div className="font-heading text-xl mt-1">{municipioNome}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#334155] text-[#94A3B8]" data-testid="signatario-form-close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="uppercase-label block mb-2">Nome completo *</label>
            <input
              data-testid="signatario-nome"
              required
              type="text"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              placeholder="Ex.: João da Silva Santos"
              className="tce-input w-full px-3 py-2.5 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="uppercase-label block mb-2">Cargo / Função *</label>
            <input
              data-testid="signatario-cargo"
              required
              type="text"
              list="cargos-sugeridos"
              value={form.cargo}
              onChange={(e) => setForm({ ...form, cargo: e.target.value })}
              placeholder="Ex.: Prefeito Municipal"
              className="tce-input w-full px-3 py-2.5 rounded-md text-sm"
            />
            <datalist id="cargos-sugeridos">
              {cargosSugeridos.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div>
            <label className="uppercase-label block mb-2">CPF (opcional)</label>
            <input
              data-testid="signatario-cpf"
              type="text"
              value={form.cpf}
              onChange={(e) => setForm({ ...form, cpf: e.target.value })}
              placeholder="000.000.000-00"
              className="tce-input w-full px-3 py-2.5 rounded-md text-sm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-sm">
              Cancelar
            </button>
            <button data-testid="signatario-form-save" disabled={saving}
                    className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm disabled:opacity-50">
              {saving ? "Salvando…" : (initial ? "Atualizar" : "Cadastrar")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
